import React, { CSSProperties } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { observer } from 'mobx-react';

import { ITooltipPayload } from '../../../../../models/charting/index.js';
import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import Superscript from '../../../../base/Superscript/Superscript.js';
import ChangeIndicator from '../../../../overview/ChangeIndicator/ChangeIndicator.js';

import {
	IDefaultTooltipContentConfiguration,
	IDefaultTooltipContentProps,
	IDefaultTooltipContentState,
} from './models/index.js';
import {
	DEFAULT_TOOLTIP_CONTENT_CATEGORY_KEY,
	DEFAULT_TOOLTIP_CONTENT_COMPONENT_KEY,
	DEFAULT_TOOLTIP_CONTENT_PARENT_COMPONENT_KEY,
} from './DefaultTooltipContent.meta.js';

interface IWorkingDefaultTooltipContentProps extends IDefaultTooltipContentProps {
	/**
	 * used internally to fix an issue with the Radial Bar Chart tooltip content
	 * https://github.com/recharts/recharts/issues/1092
	 */
	usePayloadName?: boolean;
	_globalDisclaimers?: Map<string, any[]>;
}

/**
 * This is a custom implementation of the DefaultTooltipContent to address the incorrect label being applied
 * https://github.com/recharts/recharts/blob/b225fb67d6c5fb500d8d8e416a764e36f5a32000/src/component/DefaultTooltipContent.js#L16
 *
 * At time of writing this is broken all the way up to recharts (v2.1.2)
 * https://github.com/recharts/recharts/issues/1092
 *
 * We should look into how we can submit a permanent fix on recharts repo (by addressing what the radial chart passes as the label)
 */
export class DefaultTooltipContent extends ComponentBase<
	IWorkingDefaultTooltipContentProps,
	IDefaultTooltipContentState,
	IDefaultTooltipContentConfiguration
> {
	categoryKey = DEFAULT_TOOLTIP_CONTENT_CATEGORY_KEY;
	componentKey = DEFAULT_TOOLTIP_CONTENT_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return DEFAULT_TOOLTIP_CONTENT_PARENT_COMPONENT_KEY;
	}

	constructor(props: IWorkingDefaultTooltipContentProps) {
		super(props);
	}

	defaultFormatter = (value: any) =>
		Array.isArray(value) && this.isNumOrStr(value[0]) && this.isNumOrStr(value[1]) ? value.join(' ~ ') : value;

	isNumOrStr = (value: ITooltipPayload['value']) =>
		common.commonUtils.isNumber(value) ||
		(typeof value === 'string' && !common.commonUtils.isNullOrWhitespace(value));

	renderContent() {
		const { payload, separator, formatter, itemStyle, valueStyle, itemSorter, _globalDisclaimers, changeValues } =
			this.props;

		if (payload && payload.length) {
			const listStyle = { padding: 0, margin: 0 };

			const items = (itemSorter ? common.commonUtils.sortBy(payload, itemSorter as any) : payload).map(
				(entry, i) => {
					if (entry.type === 'none') {
						return null;
					}

					const finalItemStyle = {
						display: 'block',
						paddingTop: 4,
						paddingBottom: 4,
						color: entry.color || '#000',
						...itemStyle,
					};
					// using `any` for weird recharts typing....
					const finalFormatter: any = formatter || entry.formatter || this.defaultFormatter;
					let { name, value } = entry;
					if (finalFormatter && value != null && name != null) {
						const formatted = finalFormatter(value, name, entry, i, payload);
						if (Array.isArray(formatted)) {
							[value, name] = formatted;
						} else {
							value = formatted;
						}
					}

					const superscripts: number[] = [];
					const { dataKey } = entry as any;
					const entryDisclaimers = _globalDisclaimers?.get(dataKey);
					if (entryDisclaimers) {
						// Loop through and build up the superscripts from the global disclaimers
						for (let index = 0; index < entryDisclaimers.length; index++) {
							const disclaimer = entryDisclaimers[index];
							superscripts.push(disclaimer.superscript);
						}
					}

					// Get the change value
					let changeValue;
					if (changeValues && entry.payload) {
						const { mapping, formatter } = changeValues;
						const changeValueKey = mapping && mapping[dataKey];
						changeValue = changeValueKey && entry.payload[changeValueKey];
						if (formatter) {
							changeValue = common.commonUtils.formatter(formatter, changeValue);
						}
					}

					const finalValueStyle =
						valueStyle &&
						JSON.parse(JSON.stringify(valueStyle).replace('{seriesColor}', entry.color || '#000'));

					// Create superscript text by joining applicable values
					const superscriptText: string = superscripts.join(', ');
					let superscript;
					if (superscriptText) {
						superscript = <Superscript>{superscriptText}</Superscript>;
					}
					return (
						<li className="recharts-tooltip-item" key={`tooltip-item-${i}`} style={finalItemStyle}>
							{this.isNumOrStr(name) ? <span className="recharts-tooltip-item-name">{name}</span> : null}
							{this.isNumOrStr(name) ? (
								<span className="recharts-tooltip-item-separator">{separator}</span>
							) : null}
							<span className="recharts-tooltip-item-value" style={finalValueStyle}>
								{value}
							</span>
							<span className="recharts-tooltip-item-unit">{entry.unit || ''}</span>
							{changeValue && (
								<span className="recharts-tooltip-item-change-indicator">
									<ChangeIndicator {...changeValues?.changeIndicatorProps} value={changeValue} />
								</span>
							)}
							{superscript}
						</li>
					);
				},
			);

			return (
				<ul className="recharts-tooltip-item-list" style={listStyle}>
					{items}
				</ul>
			);
		}

		return null;
	}

	render() {
		const {
			wrapperClassName,
			contentStyle,
			labelClassName,
			labelStyle,
			label,
			labelFormatter,
			payload,
			usePayloadName,
		} = this.props;
		const finalStyle: CSSProperties = {
			margin: 0,
			padding: 10,
			backgroundColor: '#fff',
			border: '1px solid #ccc',
			whiteSpace: 'nowrap',
			...contentStyle,
		};
		const finalLabelStyle = {
			margin: 0,
			...labelStyle,
		};
		const workingLabel = usePayloadName === true ? payload && payload[0]?.payload?.name : label;
		const hasLabel = !common.commonUtils.isNullOrUndefined(workingLabel);

		let finalLabel = hasLabel ? workingLabel : '';
		const wrapperCN = `recharts-default-tooltip ${wrapperClassName}`;
		const labelCN = `recharts-tooltip-label ${labelClassName}`;

		if (hasLabel && labelFormatter && payload !== undefined && payload !== null) {
			finalLabel = (labelFormatter as (label: any, payload: Array<any>) => any)(workingLabel, payload);
		}

		return (
			<div className={wrapperCN} style={finalStyle}>
				<p className={labelCN} style={finalLabelStyle}>
					{React.isValidElement(finalLabel) ? finalLabel : `${finalLabel}`}
				</p>
				{this.renderContent()}
			</div>
		);
	}
}

export default InjectedStyledComponent(
	observer(DefaultTooltipContent),
	DEFAULT_TOOLTIP_CONTENT_CATEGORY_KEY,
	DEFAULT_TOOLTIP_CONTENT_PARENT_COMPONENT_KEY,
	[DEFAULT_TOOLTIP_CONTENT_COMPONENT_KEY],
);
